import React, { useState, useEffect, useMemo } from "react";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import DoneIcon from '@mui/icons-material/Done';
import ReplayIcon from '@mui/icons-material/Replay';


const PreviewImage = ({open, handlePreviewClose, files, eye}) => {
    const [imageSrc, setImageSrc] = useState('');

    // Detect Safari browser
    const isSafari = useMemo(() => {
        return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    }, []);

    // Get the file object from props
    const fileObj = useMemo(() => {
        if (Array.isArray(files) && files.length > 0) {
            return files[0].file;
        }
        return files;
    }, [files]);

    // Convert file to image source with proper cleanup
    useEffect(() => {
        if (!open) {
            // Reset image source when dialog closes
            setImageSrc('');
            return;
        }

        let blobUrl = null;
        let isCancelled = false;

        const createImageSrc = async () => {
            if (!fileObj) {
                if (!isCancelled) setImageSrc('');
                return;
            }
            
            // Check if it's an empty object
            if (typeof fileObj === 'object' && !(fileObj instanceof File) && !(fileObj instanceof Blob) && Object.keys(fileObj).length === 0) {
                if (!isCancelled) setImageSrc('');
                return;
            }
            
            // If it's already a URL (string), use it directly
            if (typeof fileObj === 'string') {
                if (!isCancelled) setImageSrc(fileObj);
                return;
            }
            
            try {
                // CRITICAL: Check for File/Blob FIRST - before any other checks!
                // File objects don't have enumerable keys, so Object.keys() returns []
                if (fileObj instanceof File || fileObj instanceof Blob) {
                    blobUrl = URL.createObjectURL(fileObj);
                    if (!isCancelled) setImageSrc(blobUrl);
                    return;
                }
                
                // Handle ArrayBuffer format (from IndexedDB storage)
                if (fileObj && typeof fileObj === 'object' && fileObj.data && fileObj.type) {
                    // Check if data is ArrayBuffer or needs conversion
                    let arrayBuffer = fileObj.data;
                    
                    // If data is already an ArrayBuffer, use it directly
                    if (arrayBuffer instanceof ArrayBuffer) {
                        const blob = new Blob([arrayBuffer], { type: fileObj.type });
                        blobUrl = URL.createObjectURL(blob);
                        if (!isCancelled) setImageSrc(blobUrl);
                        return;
                    }
                    
                    // If data is a Uint8Array or similar, convert to ArrayBuffer
                    if (arrayBuffer instanceof Uint8Array || Array.isArray(arrayBuffer)) {
                        const blob = new Blob([arrayBuffer], { type: fileObj.type });
                        blobUrl = URL.createObjectURL(blob);
                        if (!isCancelled) setImageSrc(blobUrl);
                        return;
                    }
                }
                
                // If it's an object with a file property, try to get the file
                if (fileObj && typeof fileObj === 'object' && fileObj.file) {
                    const nestedFile = fileObj.file;
                    if (nestedFile instanceof File || nestedFile instanceof Blob) {
                        blobUrl = URL.createObjectURL(nestedFile);
                        if (!isCancelled) setImageSrc(blobUrl);
                        return;
                    }
                    // If nested file is ArrayBuffer format
                    if (nestedFile && typeof nestedFile === 'object' && nestedFile.data && nestedFile.type) {
                        const blob = new Blob([nestedFile.data], { type: nestedFile.type });
                        blobUrl = URL.createObjectURL(blob);
                        if (!isCancelled) setImageSrc(blobUrl);
                        return;
                    }
                }
                
                // Fallback - return empty string
                if (!isCancelled) setImageSrc('');
            } catch (error) {
                console.error('Error creating image source:', error);
                if (!isCancelled) setImageSrc('');
            }
        };

        createImageSrc();

        // Cleanup: revoke blob URL when component unmounts, dialog closes, or file changes
        return () => {
            isCancelled = true;
            if (blobUrl) {
                try {
                    URL.revokeObjectURL(blobUrl);
                } catch (error) {
                    // Ignore errors when revoking (URL might already be revoked)
                }
            }
        };
    }, [fileObj, isSafari, open]);

    // const [answer, setAnswer] = useState(false);

    // const handleAnswerClick = (value) => {
    //     setAnswer(true);
    // };

    // const handleUndoClick = () => {
    //     setAnswer(false);
    // }
  return (
    <div>
        <Dialog open={open} fullWidth={true} maxWidth="lg" height="md">
            <DialogTitle display="flex" justifyContent="space-between" alignItems="center">
                <Typography fontWeight={500}  color="#0D1E83">
                    Captured Image of {eye}
                </Typography>
                <Button endIcon={<CloseIcon />} onClick={handlePreviewClose} sx={{color: "#0D1E83", textTransform:"none"}}>Close</Button>
            </DialogTitle>
            <DialogContent sx={{
                display: "flex", 
                justifyContent: "center", 
                alignItems: "center",
                padding: "20px",
                minHeight: "70vh"
            }}>
                <Box sx={{ width: "100%", display: "flex", justifyContent: "center" }}>
                    {imageSrc ? (
                        <img
                            src={imageSrc}
                            alt={fileObj?.name || 'image'}
                            onError={(e) => {
                                console.error('Image load error, attempting fallback methods...', fileObj);
                                
                                // Try to recreate image source using different methods
                                const tryFallback = async () => {
                                    try {
                                        // Method 1: If it's a File/Blob, try data URL
                                        if (fileObj && (fileObj instanceof File || fileObj instanceof Blob)) {
                                            const reader = new FileReader();
                                            reader.onload = () => {
                                                setImageSrc(reader.result);
                                            };
                                            reader.onerror = () => {
                                                console.error('FileReader failed');
                                                setImageSrc(''); // Clear invalid source
                                            };
                                            reader.readAsDataURL(fileObj);
                                            return;
                                        }
                                        
                                        // Method 2: If it's ArrayBuffer format, try recreating blob
                                        if (fileObj && typeof fileObj === 'object' && fileObj.data && fileObj.type) {
                                            try {
                                                const blob = new Blob([fileObj.data], { type: fileObj.type });
                                                const newBlobUrl = URL.createObjectURL(blob);
                                                setImageSrc(newBlobUrl);
                                                return;
                                            } catch (blobError) {
                                                console.error('Failed to create blob from ArrayBuffer:', blobError);
                                            }
                                        }
                                        
                                        // Method 3: If nested file exists
                                        if (fileObj && typeof fileObj === 'object' && fileObj.file) {
                                            const nestedFile = fileObj.file;
                                            if (nestedFile instanceof File || nestedFile instanceof Blob) {
                                                const reader = new FileReader();
                                                reader.onload = () => {
                                                    setImageSrc(reader.result);
                                                };
                                                reader.onerror = () => {
                                                    setImageSrc('');
                                                };
                                                reader.readAsDataURL(nestedFile);
                                                return;
                                            }
                                        }
                                        
                                        // All methods failed
                                        setImageSrc('');
                                    } catch (error) {
                                        console.error('All fallback methods failed:', error);
                                        setImageSrc('');
                                    }
                                };
                                
                                tryFallback();
                            }}
                            style={{ 
                                borderRadius: "4px", 
                                width: "100%", 
                                height: "auto", 
                                maxWidth: "100%",
                                objectFit: "contain"
                            }}
                        />
                    ) : (
                        <Typography color="error">Image failed to load. Please try uploading again.</Typography>
                    )}
                </Box>
            </DialogContent>
            {/* <DialogActions>
                <Box width="md" display="flex" justifyContent="flex-end" alignItems="center" paddingRight="25px">
                    {
                        answer ?
                        (<>
                            <Typography fontWeight={500} fontSize="16px" mr="8px">
                                Gradeability Feedback is saved. Close this window to proceed.
                            </Typography>
                            <Button
                                borderRadius="8px"
                                sx={{
                                    color: "#0D1E83",
                                    border: "1px solid #0000000D"
                                }}
                                startIcon={<ReplayIcon />}
                                onClick={handleUndoClick}
                            >
                                Undo
                            </Button>
                        </>)
                        :
                        (<>
                            <Typography fontWeight={500} fontSize="16px" mr="8px">Is the Image Gradable?</Typography>
                            <Button
                                borderRadius="8px"
                                sx={{
                                    color: "#075400",
                                    background: "#C7F5B7",
                                    border: "1px solid #C7F5B7",
                                    marginRight: "8px"
                                }}
                                startIcon={<DoneIcon />}
                                onClick={handleAnswerClick}
                            >
                                    Yes
                            </Button>
                            <Button
                                borderRadius="8px"
                                sx={{
                                    color: "#9F0101",
                                    background: "#FDD2D2",
                                    border: "1px solid #FDD2D2",
                                }}
                                startIcon={<CloseIcon />}
                                onClick={handleAnswerClick}
                            >
                                No
                            </Button>
                        </>)
                    }
                </Box>
            </DialogActions> */}
        </Dialog>
    </div>
  )
}

export default PreviewImage